import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, exhaustMap, map, of, switchMap, take, timer } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { SessionActions } from "../../authentication/store/session.actions";
import { CompanyBillingApiService } from "../data-access/company-billing-api.service";
import { CompanyEntitlementActions } from "./company-entitlement.actions";
import { SubscriptionsActions } from "./subscriptions.actions";

@Injectable()
export class SubscriptionsEffects {
    private readonly actions$ = inject(Actions);
    private readonly api = inject(CompanyBillingApiService);
    private readonly translate = inject(TranslateService);

    readonly loadPlans$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SubscriptionsActions.pageOpened),
            exhaustMap(() =>
                this.api.loadPlans().pipe(
                    map((plans) => SubscriptionsActions.plansLoaded({ plans })),
                    catchError((error: unknown) =>
                        of(
                            SubscriptionsActions.plansLoadFailed({
                                message: this.message(error),
                            })
                        )
                    )
                )
            )
        )
    );

    readonly checkout$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SubscriptionsActions.invoiceCheckoutSubmitted),
            exhaustMap(({ clientId, request }) =>
                this.api.subscribeByInvoice(clientId, request).pipe(
                    map(() => SubscriptionsActions.checkoutSucceeded()),
                    catchError((error: unknown) =>
                        of(
                            SubscriptionsActions.checkoutFailed({
                                message: this.message(error),
                            })
                        )
                    )
                )
            )
        )
    );

    readonly refreshEntitlement$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SubscriptionsActions.checkoutSucceeded),
            switchMap(() =>
                timer(0, 3000).pipe(
                    take(5),
                    map(() => CompanyEntitlementActions.refreshRequested())
                )
            )
        )
    );

    readonly resetOnLogout$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SessionActions.logoutCompleted),
            map(() => SubscriptionsActions.reset())
        )
    );

    private message(error: unknown): string {
        return error instanceof Error
            ? error.message
            : this.translate.instant("billing.errors.requestFailed");
    }
}
